import React, { useState, useEffect, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import InvoiceListFilters from "./InvoiceListFilters";
import {
  exportInvoices,
  getInvoices,
  getInvoiesAsCsv,
} from "../../actions/myobActions";
import MYOBList from "./InvoiceListViewTable";
import { mustBeArray, requestHasError } from "../../utils/lib";
import { push } from "../../utils/navigation";
import {
  BASE_URL,
  DEFAULT_DATE_FORMAT,
  EXPORTED_STATUS_ID,
  READY_FOR_EXPORT_STATUS_ID,
} from "../../constants";
import { toastr } from "react-redux-toastr";

const defaultFilter = { from: "", to: "", clientName: "", status: "" };

const InvoiceListView = () => {
  const dispatch = useDispatch();

  const [isSearching, setSearching] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [exportInvoiceList, setExportInvoiceList] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const allSelected = useMemo(() => invoiceList.filter(x => x.canExport).length === selectedRows.length, [invoiceList, selectedRows]);
  const [filters, setFilters] = useState(defaultFilter);

  async function fetchInvoiceList(hasFilters = false) {
    let currentFilters = hasFilters
      ? {
          from: filters.dateFrom,
          to: filters.dateTo,
          clientName: filters.clientName,
          status: filters.status,
        }
      : defaultFilter;
    try {
      setSearching(true);
      const res = await getInvoices(currentFilters);
      if (res) {
        setInvoiceList(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSearching(false);
    }
  }

  const handleFilterChange = ({ value, field }) => {
    if (["dateFrom", "dateTo"].includes(field)) {
      value = value.format(DEFAULT_DATE_FORMAT);
    }
    const updatedFilters = { ...filters, [field]: value };
    setFilters(updatedFilters);
  };

  const handleResetFilter = () => {
    setFilters(defaultFilter);
    fetchInvoiceList();
  };
  const handleSearch = () => {
    fetchInvoiceList(true);
  };

  useEffect(() => {
    fetchInvoiceList(true);
  }, []);

  const handleCheckboxSelection = (row) => {
    let updatedSelectedRows = [];
    if (selectedRows.includes(row.id)) {
      updatedSelectedRows = selectedRows.filter((a) => a !== row.id);
    } else {
      updatedSelectedRows = [...selectedRows, row.id];
    }
    setSelectedRows(updatedSelectedRows);
  };

  const handleAllSelected = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(invoiceList.filter(x => x.canExport));
    }
  };

  const handleBulkDownload = async () => {
    setBulkDownloading(true);
    try {
      await getInvoiesAsCsv(filters);
      toastr.success("Report Downloaded");
    } catch (error) {
      toastr.error("Failed to download report");
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleCreate = () => {
    dispatch(push(`${BASE_URL}/invoice/create`));
  };

  async function handleBulkExport() {
    if (_.isEmpty(selectedRows)) {
      return toastr.error("Bulk Export Error: Invoices are required.");
    }
    setBulkExporting(true);
    exportInvoices(selectedRows).finally(() => {
      setBulkExporting(false);
      setSelectedRows([]);
      fetchInvoiceList(true);
    });
  }

  const handleExport = async ({ data }) => {
    setExportInvoiceList([...exportInvoiceList, data.id]);
    await exportInvoices([data.id]);
    await fetchInvoiceList();
    setSelectedRows([]);
    toastr.success("Invoice Exported");
  };

  /**
   * @param {import("./InvoiceListViewTable").Invoice} item
   */
  function handleView(item) {
    dispatch(push(`${BASE_URL}/invoice/${item.id}`));
  }

  return (
    <div className="mb-1">
      <div className={"row"}>
        <div className={"col-12"} style={{ marginBottom: "24px" }}>
          <h4>Invoices</h4>
        </div>
      </div>
      <Row className="mb-2">
        <Col sm={12} md={7} lg={6}>
          <InvoiceListFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleCreate={handleCreate}
            handleSearch={handleSearch}
            handleResetFilter={handleResetFilter}
          />
        </Col>
      </Row>
      <div className="mb-1 rcti-list-action-buttons">
        <button
          className={`btn btn-success bold ${
            bulkExporting ? "no-pointer-events" : ""
          }`}
          onClick={handleBulkExport}
        >
          {bulkExporting ? "Exporting" : "Bulk Export"}
        </button>
        <button
          className={`btn btn-primary ml-1 ${
            bulkDownloading ? "no-pointer-events" : ""
          }`}
          onClick={handleBulkDownload}
          disabled={bulkDownloading}
        >
          <i className="fa fa-arrow-down" aria-hidden="true"></i> &nbsp;
          {bulkDownloading ? "Downloading" : "Download"}
        </button>
      </div>
      <div className="clearfix" />
      <MYOBList
        handleCheckboxSelection={handleCheckboxSelection}
        handleAllSelected={handleAllSelected}
        allSelected={allSelected}
        selectedRows={mustBeArray(selectedRows)}
        handleView={handleView}
        handleExport={handleExport}
        exportingRebates={mustBeArray(exportInvoiceList)}
        invoiceList={mustBeArray(invoiceList)}
        searching={isSearching}
      />
    </div>
  );
};

export default InvoiceListView;
